// Neteo intercompañía y cuentas ignoradas de una empresa consolidada
// (pestaña «Consolidación» de la ficha; plan consolidadas, decisiones §17).
// El state guarda la lista vigente (ajustes + resumen por miembro) de la
// consolidada abierta; las opciones de cuentas y el preview se piden por
// demanda y también se guardan para que los componentes sean presentacionales.
import {
    deleteConsolidatedAdjustment as deleteConsolidatedAdjustmentApi,
    getConsolidatedAccountOptions,
    getConsolidatedAdjustments,
    previewConsolidatedAdjustment as previewConsolidatedAdjustmentApi,
    saveConsolidatedAdjustment as saveConsolidatedAdjustmentApi,
} from "@/api/carteraAnalista";

export const KIND_NETTING_BALANCE = "netting_balance";
export const KIND_NETTING_RESULTS = "netting_results";
export const KIND_IGNORE = "ignore";

const state = () => ({
    companyId: null,
    adjustments: [],
    members: [],
    // { "<kind>:<side>": [{ company_id, name, accounts }] } — una entrada por
    // combinación pedida, así los dos lados del editor conviven.
    options: {},
    // { [kind]: { left_total, right_total, residual_total, init_date, end_date } }:
    // ajuste del bloque sobre la unión de sus cuentas (D14), no la suma de residuos.
    blockTotals: {},
    preview: null,
    loading: false,
    saving: false,
    error: null,
});

const mutations = {
    SET_COMPANY_ID: (state, payload) => {
        state.companyId = payload;
    },
    SET_ADJUSTMENTS: (state, { adjustments, members, block_totals }) => {
        state.adjustments = adjustments || [];
        state.members = members || [];
        state.blockTotals = block_totals || {};
    },
    SET_OPTIONS: (state, { key, options }) => {
        state.options = { ...state.options, [key]: options };
    },
    SET_PREVIEW: (state, payload) => {
        state.preview = payload;
    },
    SET_LOADING: (state, payload) => {
        state.loading = payload;
    },
    SET_SAVING: (state, payload) => {
        state.saving = payload;
    },
    SET_ERROR: (state, payload) => {
        state.error = payload;
    },
};

export const optionsKey = (kind, side) => `${kind}:${side || "left"}`;

const actions = {
    // Lista vigente de la consolidada. Relanza el error: la vista lo reporta.
    async fetchAdjustments({ commit }, companyId) {
        commit("SET_COMPANY_ID", companyId);
        commit("SET_LOADING", true);
        commit("SET_ERROR", null);
        try {
            const { data } = await getConsolidatedAdjustments(companyId);
            commit("SET_ADJUSTMENTS", data);
            return data;
        } catch (error) {
            commit("SET_ERROR", error);
            throw error;
        } finally {
            commit("SET_LOADING", false);
        }
    },
    // Cuentas elegibles por miembro para `kind`/`side` (sin las ya usadas,
    // salvo las del ajuste en edición).
    async fetchAccountOptions({ commit, state }, { kind, side = "left", adjustmentId = null }) {
        const { data } = await getConsolidatedAccountOptions(state.companyId, {
            kind,
            side,
            adjustment_id: adjustmentId,
        });
        commit("SET_OPTIONS", { key: optionsKey(kind, side), options: data });
        return data;
    },
    async previewAdjustment({ commit, state }, { kind, lines, adjustmentId = null }) {
        const { data } = await previewConsolidatedAdjustmentApi(state.companyId, {
            kind,
            lines,
            adjustment_id: adjustmentId,
        });
        commit("SET_PREVIEW", data);
        return data;
    },
    clearPreview({ commit }) {
        commit("SET_PREVIEW", null);
    },
    // Guarda y deja la lista como la devuelve el backend (fuente de verdad).
    async saveAdjustment({ commit, state }, { kind, label, lines, adjustmentId = null }) {
        commit("SET_SAVING", true);
        try {
            const { data } = await saveConsolidatedAdjustmentApi(state.companyId, {
                kind,
                label,
                lines,
                adjustment_id: adjustmentId,
            });
            commit("SET_ADJUSTMENTS", data);
            commit("SET_PREVIEW", null);
            return data;
        } finally {
            commit("SET_SAVING", false);
        }
    },
    async deleteAdjustment({ commit, state }, adjustmentId) {
        commit("SET_SAVING", true);
        try {
            const { data } = await deleteConsolidatedAdjustmentApi(state.companyId, adjustmentId);
            commit("SET_ADJUSTMENTS", data);
            return data;
        } finally {
            commit("SET_SAVING", false);
        }
    },
};

const getters = {
    nettings: (state) => state.adjustments.filter((a) => a.kind !== KIND_IGNORE),
    balanceNettings: (state) => state.adjustments.filter((a) => a.kind === KIND_NETTING_BALANCE),
    resultsNettings: (state) => state.adjustments.filter((a) => a.kind === KIND_NETTING_RESULTS),
    ignoreAdjustments: (state) => state.adjustments.filter((a) => a.kind === KIND_IGNORE),
    // Líneas ignoradas agrupadas por miembro: { [member_company_id]: [line] }.
    ignoredByMember: (state, getters) => {
        const out = {};
        for (const adjustment of getters.ignoreAdjustments) {
            for (const line of adjustment.lines) {
                (out[line.member_company_id] = out[line.member_company_id] || []).push(line);
            }
        }
        return out;
    },
    optionsFor: (state) => (kind, side) => state.options[optionsKey(kind, side)] || [],
    blockTotal: (state) => (kind) => state.blockTotals[kind] || null,
};

export default {
    namespaced: true,
    state,
    mutations,
    actions,
    getters,
};
